import { supabase } from "../../lib/supabase/client"

export interface UserProfile {
  userId: string
  email: string
  fullName: string | null
  preferredName: string | null
  userName: string // Computed: preferredName || firstName || ""
  organizationId: string | null
}

// Cache for user data with 5-minute expiration (same as session cache)
let userCache: {
  data: UserProfile | null
  timestamp: number
} | null = null

const USER_CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

/**
 * Get the current user's profile data from the database with caching
 * Falls back to auth metadata if database fetch fails
 */
export async function getCurrentUserProfile(): Promise<UserProfile | null> {
  // Check cache first
  if (userCache && Date.now() - userCache.timestamp < USER_CACHE_DURATION) {
    return userCache.data
  }

  const { data: { session } } = await supabase.auth.getSession()
  
  if (!session) {
    userCache = { data: null, timestamp: Date.now() }
    return null
  }

  const { data: userData, error: userFetchError } = await supabase
    .from("users")
    .select("organization_id, full_name, preferred_name")
    .eq("id", session.user.id)
    .maybeSingle()

  let profileData: UserProfile

  if (userFetchError) {
    console.error("Failed to fetch user profile:", userFetchError)
    // Return minimal profile from session data
    profileData = {
      userId: session.user.id,
      email: session.user.email || '',
      fullName: session.user.user_metadata?.full_name || null,
      preferredName: session.user.user_metadata?.preferred_name || null,
      userName: session.user.user_metadata?.full_name?.split(" ")[0] || "",
      organizationId: session.user.user_metadata?.org_id || null
    }
  } else {
    // Prefer database fields, fallback to metadata
    const fullName = userData?.full_name || session.user.user_metadata?.full_name || null
    const preferredName = userData?.preferred_name || session.user.user_metadata?.preferred_name || null
    const userName = preferredName || fullName?.split(" ")[0] || ""
    
    // Handle the org_id vs organization_id inconsistency
    const organizationId = userData?.organization_id || session.user.user_metadata?.org_id || null

    profileData = {
      userId: session.user.id,
      email: session.user.email || '',
      fullName,
      preferredName,
      userName,
      organizationId
    }
  }

  // Cache the result
  userCache = { data: profileData, timestamp: Date.now() }
  return profileData
}

/**
 * Invalidate the user cache (call after profile updates)
 */
export function invalidateUserCache() {
  userCache = null
}